(() => {
  const STORAGE_KEY = 'mobilegs:mobiskin-cart-addon:v1';
  const SOFT_CLOSE_EVENT = 'mobilegs:mobiskin-soft-close';
  const ADDED_EVENT = 'mobilegs:mobiskin-added';
  const ANALYTICS_EVENT = 'mobilegs:mobiskin-addon-analytics';
  const SHOPIFY_ANALYTICS_PREFIX = 'mobilegs:';
  const PRODUCT_CACHE_TTL = 2 * 60 * 1000;
  const DESKTOP_CART_MEDIA_QUERY = '(min-width: 990px)';
  const isLocalFallbackPreview = () => {
    const host = window.location.hostname;
    return (
      (host === '127.0.0.1' || host === 'localhost') &&
      new URLSearchParams(window.location.search).get('mobiskin_preview') === 'fallback'
    );
  };
  const productRequests = new Map();

  const defaultState = () => ({
    declined: false,
    softClosed: false,
    fallbackShown: false,
    addedViaOffer: false,
    hasImpression: false,
    checkoutTracked: false,
    lastTriggerVariantId: null,
    lastTriggerAt: null,
  });

  function readState() {
    try {
      return { ...defaultState(), ...JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') };
    } catch (error) {
      return defaultState();
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // The offer still works when storage is blocked; only repetition controls degrade.
    }
  }

  function parseIds(value) {
    return new Set(
      String(value || '')
        .split(/[\s,]+/)
        .map((id) => Number(id))
        .filter(Number.isFinite)
    );
  }

  function emitAnalytics(action, detail = {}) {
    const payload = {
      event: `mobiskin_addon_${action}`,
      mobiskin_action: action,
      ...detail,
    };

    document.dispatchEvent(new CustomEvent(ANALYTICS_EVENT, { detail: payload }));

    const customerEvent = window.Shopify?.analytics?.publish?.(`${SHOPIFY_ANALYTICS_PREFIX}${payload.event}`, payload);
    customerEvent?.catch?.(() => {});

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  }

  function isTruthyProperty(value) {
    if (value === true || value === 1) return true;
    return ['1', 'true', 'yes', 'included'].includes(String(value || '').trim().toLowerCase());
  }

  function installCheckoutTracking() {
    if (window.__mobilegsMobiskinCheckoutTracking) return;
    window.__mobilegsMobiskinCheckoutTracking = true;

    const trackCheckout = (surface) => {
      const state = readState();
      if (!state.hasImpression || state.checkoutTracked) return;

      state.checkoutTracked = true;
      writeState(state);
      emitAnalytics('checkout_started_after_impression', { mobiskin_surface: surface });
    };

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest(
          '[name="checkout"], #checkout, .additional-checkout-buttons, .shopify-payment-button'
        );
        if (!target) return;
        trackCheckout(target.closest('cart-notification') ? 'notification' : 'cart');
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.name !== 'checkout') return;
        trackCheckout(event.target.closest('cart-notification') ? 'notification' : 'cart');
      },
      true
    );
  }

  class MobiskinCartAddon extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.surface = this.dataset.surface || 'notification';
      this.config = this.readConfig();
      if (!this.config?.productId) return;

      this.excludedVariantIds = parseIds(this.config.excludedVariantIds);
      this.coveredVariantIds = parseIds(this.config.coveredVariantIds);
      this.variants = Array.isArray(this.config.variants) ? this.config.variants : [];
      this.select = this.querySelector('[data-mobiskin-variant]');
      this.image = this.querySelector('[data-mobiskin-image], .mobiskin-addon__image');
      this.price = this.querySelector('[data-mobiskin-price]');
      this.comparePrice = this.querySelector('[data-mobiskin-compare-price]');
      this.buttonPrice = this.querySelector('[data-mobiskin-button-price]');
      this.addButton = this.querySelector('[data-mobiskin-add]');
      this.loadingSpinner = this.addButton?.querySelector('.loading__spinner');
      this.declineButton = this.querySelector('[data-mobiskin-decline]');
      this.controls = this.querySelector('[data-mobiskin-controls]');
      this.status = this.querySelector('[data-mobiskin-status]');
      this.trackedImpressions = new Set();
      this.eligibilitySequence = 0;
      this.cartEvaluationSequence = 0;

      this.select?.addEventListener('change', () => this.handleVariantChange());
      this.addButton?.addEventListener('click', () => this.addSelectedVariant());
      this.declineButton?.addEventListener('click', () => this.decline());
      document.addEventListener(SOFT_CLOSE_EVENT, this.handleSoftCloseEvent);
      document.addEventListener(ADDED_EVENT, this.handleAddedEvent);

      if (
        this.surface === 'cart' &&
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.handleCartUpdate(event));
      }

      if (this.surface === 'cart') {
        this.cartDisplayMediaQuery = window.matchMedia(DESKTOP_CART_MEDIA_QUERY);
        if (typeof this.cartDisplayMediaQuery.addEventListener === 'function') {
          this.cartDisplayMediaQuery.addEventListener('change', this.handleCartBreakpointChange);
        } else {
          this.cartDisplayMediaQuery.addListener?.(this.handleCartBreakpointChange);
        }
      }

      installCheckoutTracking();
      this.renderVariants();

      if (this.surface === 'cart') {
        this.evaluateCartReminder();
      }
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      if (typeof this.cartDisplayMediaQuery?.removeEventListener === 'function') {
        this.cartDisplayMediaQuery.removeEventListener('change', this.handleCartBreakpointChange);
      } else {
        this.cartDisplayMediaQuery?.removeListener?.(this.handleCartBreakpointChange);
      }
      document.removeEventListener(SOFT_CLOSE_EVENT, this.handleSoftCloseEvent);
      document.removeEventListener(ADDED_EVENT, this.handleAddedEvent);
    }

    handleSoftCloseEvent = () => {
      if (this.surface === 'cart') this.evaluateCartReminder();
    };

    handleAddedEvent = (event) => {
      if (event.detail?.sourceId === this.id) return;
      this.hideOffer();
    };

    handleCartBreakpointChange = () => {
      if (this.surface === 'cart') this.evaluateCartReminder();
    };

    readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-mobiskin-config]')?.textContent || '{}');
      } catch (error) {
        console.error('Unable to read Mobiskin add-on configuration.', error);
        return null;
      }
    }

    handleCartUpdate(event) {
      if (event?.source === 'mobiskin-cart-addon' && event?.sourceId === this.id) {
        this.cartHadAddon = true;
        return;
      }
      this.evaluateCartReminder(event?.cartData);
    }

    prepareForProductAdd() {
      this.eligibilitySequence += 1;
      this.hideOffer();
      if (this.controls) this.controls.hidden = false;
      this.setStatus();
    }

    async handleQualifyingAdd(event) {
      const evaluationSequence = this.eligibilitySequence;
      const variantId = Number(
        event?.productVariantId || event?.variantId || event?.cartData?.variant_id || event?.cartData?.id
      );
      const productId = Number(event?.cartData?.product_id || event?.cartData?.productId);
      const state = readState();

      if (state.declined) {
        this.hideOffer();
        return;
      }

      if (
        productId === Number(this.config.productId) ||
        this.isAddonVariant(variantId) ||
        this.excludedVariantIds.has(variantId) ||
        this.coveredVariantIds.has(variantId)
      ) {
        this.hideOffer();
        return;
      }

      let cart;
      try {
        cart = await this.fetchCart();
      } catch (error) {
        this.hideOffer();
        return;
      }

      if (evaluationSequence !== this.eligibilitySequence) return;

      if (!this.cartQualifies(cart)) {
        this.hideOffer();
        return;
      }

      await this.refreshProductData();
      if (evaluationSequence !== this.eligibilitySequence) return;

      if (!this.availableVariants().length) {
        this.hideOffer();
        return;
      }

      const triggerAt = Date.now();
      writeState({
        ...state,
        softClosed: false,
        fallbackShown: false,
        addedViaOffer: false,
        lastTriggerVariantId: variantId || null,
        lastTriggerAt: triggerAt,
      });

      emitAnalytics('eligible', {
        mobiskin_surface: 'notification',
        trigger_variant_id: variantId || null,
      });

      const notification = this.closest('.cart-notification');
      if (notification && !notification.classList.contains('active')) {
        const closedState = readState();
        closedState.softClosed = true;
        closedState.fallbackShown = false;
        writeState(closedState);
        emitAnalytics('soft_close', {
          mobiskin_surface: 'notification',
          mobiskin_offer_rendered: false,
        });
        document.dispatchEvent(new CustomEvent(SOFT_CLOSE_EVENT));
        return;
      }

      this.showOffer(`notification:${triggerAt}`);
    }

    async evaluateCartReminder(cartData) {
      if (this.surface !== 'cart') return;
      const evaluationSequence = ++this.cartEvaluationSequence;
      const isPersistentDesktop = this.isPersistentDesktopCart();

      // Local theme-review route: show the real component without writing session state or analytics.
      if (isLocalFallbackPreview()) {
        this.hidden = false;
        this.controls.hidden = false;
        this.setStatus();
        return;
      }

      const initialState = readState();
      if (
        initialState.declined ||
        (!isPersistentDesktop &&
          !initialState.addedViaOffer &&
          (!initialState.softClosed || initialState.fallbackShown))
      ) {
        this.hideOffer();
        return;
      }

      let cart = cartData;
      if (!cart?.items) {
        try {
          cart = await this.fetchCart();
        } catch (error) {
          this.hideOffer();
          return;
        }
      }
      if (evaluationSequence !== this.cartEvaluationSequence) return;

      const hasAddon = this.cartHasAddon(cart);
      const state = readState();

      if (this.cartHadAddon === true && !hasAddon && state.addedViaOffer) {
        state.addedViaOffer = false;
        state.softClosed = true;
        state.fallbackShown = false;
        state.lastTriggerAt = Date.now();
        writeState(state);
      }
      this.cartHadAddon = hasAddon;

      const currentState = readState();
      if (
        currentState.declined ||
        (!isPersistentDesktop && (!currentState.softClosed || currentState.fallbackShown)) ||
        !this.cartQualifies(cart)
      ) {
        this.hideOffer();
        return;
      }

      await this.refreshProductData();
      if (evaluationSequence !== this.cartEvaluationSequence) return;
      if (!this.availableVariants().length) {
        this.hideOffer();
        return;
      }

      if (isPersistentDesktop) {
        this.showOffer('cart:persistent', 'cart_persistent_impression');
        return;
      }

      currentState.fallbackShown = true;
      writeState(currentState);
      this.showOffer(`cart:${currentState.lastTriggerAt || Date.now()}`, 'cart_fallback_impression');
    }

    isPersistentDesktopCart() {
      return this.surface === 'cart' && (this.cartDisplayMediaQuery?.matches ?? false);
    }

    cartQualifies(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      if (!items.length || this.cartHasAddon(cart)) return false;
      if (items.some((item) => this.itemIncludesMobiskin(item))) return false;

      return items.some((item) => {
        const variantId = Number(item.variant_id || item.id);
        return (
          Number(item.product_id) !== Number(this.config.productId) &&
          !this.excludedVariantIds.has(variantId) &&
          !this.coveredVariantIds.has(variantId)
        );
      });
    }

    cartHasAddon(cart) {
      return (cart?.items || []).some(
        (item) =>
          Number(item.product_id) === Number(this.config.productId) || this.isAddonVariant(Number(item.variant_id || item.id))
      );
    }

    isAddonVariant(variantId) {
      return this.variants.some((variant) => Number(variant.id) === Number(variantId));
    }

    itemIncludesMobiskin(item) {
      if (this.coveredVariantIds.has(Number(item.variant_id || item.id))) return true;

      const properties = item.properties || {};
      return ['_mobiskin_included', '_includes_mobiskin', 'Mobiskin included'].some((key) =>
        isTruthyProperty(properties[key])
      );
    }

    async fetchCart() {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to load the cart.');
      return response.json();
    }

    async refreshProductData(force = false) {
      const requestUrl = `${this.config.productUrl}.js`;
      let request = productRequests.get(requestUrl);
      const cacheExpired = !request || Date.now() - request.fetchedAt >= PRODUCT_CACHE_TTL;

      if (force || cacheExpired) {
        request = {
          fetchedAt: Date.now(),
          promise: fetch(requestUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
            (response) => {
              if (!response.ok) throw new Error('Unable to refresh Mobiskin availability.');
              return response.json();
            }
          ),
        };
        productRequests.set(requestUrl, request);
      }

      try {
        const product = await request.promise;
        this.variants = (product.variants || []).map((variant) => ({
          id: variant.id,
          title: variant.public_title || variant.title,
          available: variant.available,
          price: variant.price,
          compareAtPrice: variant.compare_at_price || 0,
          image:
            variant.featured_image?.src ||
            variant.featured_media?.preview_image?.src ||
            product.featured_image ||
            '',
        }));
        this.renderVariants();
      } catch (error) {
        if (productRequests.get(requestUrl) === request) productRequests.delete(requestUrl);
        if (force) throw error;
      }
    }

    availableVariants() {
      return this.variants.filter((variant) => variant.available);
    }

    renderVariants() {
      if (!this.select) return;
      const available = this.availableVariants();
      const previousValue = Number(this.select.value);
      this.select.replaceChildren();

      available.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title;
        option.dataset.price = variant.price;
        option.dataset.compareAtPrice = variant.compareAtPrice || 0;
        option.dataset.imageUrl = variant.image || '';
        this.select.append(option);
      });

      if (available.some((variant) => Number(variant.id) === previousValue)) {
        this.select.value = String(previousValue);
      }

      this.updateVariantPresentation();
    }

    handleVariantChange() {
      this.updateVariantPresentation();
      emitAnalytics('design_selected', {
        mobiskin_surface: this.surface,
        addon_variant_id: Number(this.select.value),
      });
    }

    updateVariantPresentation() {
      const variant = this.variants.find((item) => Number(item.id) === Number(this.select?.value));
      if (!variant) return;

      const formattedPrice = this.formatMoney(variant.price);
      const formattedComparePrice = this.formatMoney(variant.compareAtPrice);
      if (this.price) this.price.textContent = formattedPrice;
      if (this.buttonPrice) this.buttonPrice.textContent = formattedPrice;
      if (this.comparePrice) {
        this.comparePrice.textContent = formattedComparePrice;
        this.comparePrice.hidden = !(Number(variant.compareAtPrice) > Number(variant.price));
      }
      if (this.image && variant.image) {
        this.image.removeAttribute('srcset');
        this.image.src = variant.image;
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.config.currency || window.Shopify?.currency?.active || 'USD',
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    showOffer(impressionKey, impressionAction = 'impression') {
      this.hidden = false;
      this.controls.hidden = false;
      this.setStatus();
      this.closest('.cart-notification')?.classList.add('cart-notification--with-mobiskin');

      if (this.trackedImpressions.has(impressionKey)) return;
      this.trackedImpressions.add(impressionKey);

      const state = readState();
      state.hasImpression = true;
      writeState(state);
      emitAnalytics(impressionAction, {
        mobiskin_surface: this.surface,
      });
    }

    hideOffer() {
      this.hidden = true;
      this.closest('.cart-notification')?.classList.remove('cart-notification--with-mobiskin');
    }

    recordSoftClose() {
      if (this.surface !== 'notification' || this.hidden) return;
      const state = readState();
      if (state.declined || state.addedViaOffer) return;

      state.softClosed = true;
      state.fallbackShown = false;
      writeState(state);
      emitAnalytics('soft_close', {
        mobiskin_surface: 'notification',
        mobiskin_offer_rendered: true,
      });
      this.hideOffer();
      document.dispatchEvent(new CustomEvent(SOFT_CLOSE_EVENT));
    }

    decline() {
      const state = readState();
      state.declined = true;
      state.softClosed = false;
      state.fallbackShown = true;
      writeState(state);
      emitAnalytics('explicit_dismissal', { mobiskin_surface: this.surface });
      this.hideOffer();
    }

    async addSelectedVariant() {
      const variantId = Number(this.select?.value);
      if (!variantId || this.addButton?.getAttribute('aria-disabled') === 'true') return;

      emitAnalytics('add_clicked', {
        mobiskin_surface: this.surface,
        addon_variant_id: variantId,
      });
      this.setLoading(true);
      this.setStatus();

      try {
        const formData = new FormData();
        const sections = this.sectionsToRender();
        formData.append('id', String(variantId));
        formData.append('quantity', '1');
        formData.append('sections', sections.map((section) => section.key));
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: formData,
        });
        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'This Mobiskin could not be added.');
        }

        const state = readState();
        state.addedViaOffer = true;
        state.softClosed = false;
        state.fallbackShown = true;
        writeState(state);
        this.updateRenderedSections(payload.sections || {}, sections);

        this.controls.hidden = true;
        this.setStatus('Self-apply Mobiskin added to your order.');

        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mobiskin-cart-addon',
            sourceId: this.id,
            productVariantId: variantId,
            variantId,
            cartData: payload,
          });
        }

        emitAnalytics('added', {
          mobiskin_surface: this.surface,
          addon_variant_id: variantId,
          addon_quantity: 1,
        });
        document.dispatchEvent(new CustomEvent(ADDED_EVENT, { detail: { sourceId: this.id } }));
      } catch (error) {
        const fallbackMessage = 'This Mobiskin could not be added. Please try again.';
        const rawMessage = typeof error?.message === 'string' ? error.message.trim() : '';
        const isTransportFailure =
          error instanceof TypeError ||
          error instanceof SyntaxError ||
          error?.name === 'AbortError' ||
          /failed to fetch|load failed|networkerror|network request failed/i.test(rawMessage);
        let message = isTransportFailure ? fallbackMessage : rawMessage || fallbackMessage;
        try {
          await this.refreshProductData(true);
          if (!this.availableVariants().length) {
            message = 'Mobiskins are currently unavailable.';
            this.controls.hidden = true;
          } else if (!this.availableVariants().some((variant) => Number(variant.id) === variantId)) {
            message = 'That design just sold out. Choose another available design and try again.';
          }
        } catch (refreshError) {
          // Keep the cart API's actionable error when availability cannot be refreshed.
        }
        this.setStatus(message, true);
        emitAnalytics('error', {
          mobiskin_surface: this.surface,
          addon_variant_id: variantId,
        });
      } finally {
        this.setLoading(false);
      }
    }

    sectionsToRender() {
      const sections = [
        { key: 'cart-icon-bubble', targetId: 'cart-icon-bubble', selector: '.shopify-section' },
        { key: 'cart-notification-button', targetId: 'cart-notification-button', selector: '.shopify-section' },
      ];
      const cartItems = document.getElementById('main-cart-items');
      const cartFooter = document.getElementById('main-cart-footer');

      if (cartItems?.dataset.id) {
        sections.push({ key: cartItems.dataset.id, targetId: 'main-cart-items', selector: '.js-contents' });
      }
      if (cartFooter?.dataset.id) {
        sections.push({ key: cartFooter.dataset.id, targetId: 'main-cart-footer', selector: '.js-contents' });
      }

      return sections;
    }

    updateRenderedSections(renderedSections, sections) {
      sections.forEach((section) => {
        const html = renderedSections[section.key];
        const targetRoot = document.getElementById(section.targetId);
        if (!html || !targetRoot) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(section.selector);
        const target = section.selector === '.shopify-section' ? targetRoot : targetRoot.querySelector(section.selector);
        if (source && target) target.innerHTML = source.innerHTML;
      });
    }

    setLoading(isLoading) {
      if (!this.addButton) return;
      this.addButton.setAttribute('aria-disabled', String(isLoading));
      this.addButton.classList.toggle('loading', isLoading);
      this.loadingSpinner?.classList.toggle('hidden', !isLoading);
      if (this.select) this.select.disabled = isLoading;
      if (this.declineButton) this.declineButton.disabled = isLoading;
      this.setAttribute('aria-busy', String(isLoading));
    }

    setStatus(message = '', isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.classList.toggle('is-error', Boolean(message && isError));
    }
  }

  if (!customElements.get('mobiskin-cart-addon')) {
    customElements.define('mobiskin-cart-addon', MobiskinCartAddon);
  }
})();
